import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import Loading from '@risksmart-app/components/src/loading';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import type { ObjectAccess } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import {
  classifyRiskAppetiteRemovalAction,
  useRemoveRiskAppetites,
} from '@/hooks/mutations/appetite';
import { useGetAppetiteById, useGetRiskById } from '@/hooks/queries';
import { useAggregation } from '@/hooks/useAggregation';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import { appetiteDetailsUrl } from '@/utils/urls';

const Page = () => {
  const { t: st } = useTranslation('common', { keyPrefix: 'appetites' });
  const { t } = useTranslation('common');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const appetiteId = useGetOptionalGuidParam('appetiteId');
  const riskId = useGetOptionalGuidParam('riskId');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Appetite,
    parent: null,
    hrefRoot: appetiteId ? appetiteDetailsUrl(appetiteId) : '',
  });
  const { data, loading } = useGetAppetiteById({
    queryArgs: { id: appetiteId ?? '' },
    shouldSkip: !appetiteId,
  });
  const { data: riskData, loading: riskLoading } = useGetRiskById({
    queryArgs: { riskId: riskId ?? '' },
    shouldSkip: !riskId,
  });
  const {
    appetiteAggregation,
    enableTierTwoCascading,
    loading: aggregationLoading,
  } = useAggregation();
  if (data?.appetite.length === 0) {
    throw new PageNotFound(`Appetite with id ${appetiteId} not found`);
  }
  const appetite = data?.appetite[0];
  const navigate = useNavigate();
  const removalAction = riskId
    ? classifyRiskAppetiteRemovalAction({
        appetiteAggregation,
        riskTier: riskData?.risk[0]?.Tier,
        enableTierTwoCascading,
      })
    : 'delete';
  const { removeAppetites, loading: removeLoading } = useRemoveRiskAppetites({
    riskId: riskId ?? '',
    action: removalAction,
  });
  const removalPermission: ObjectAccess | ObjectAccess[] =
    removalAction === 'unlink'
      ? ['update:linked_item', 'delete:linked_item']
      : 'delete:appetite';
  const removalParent =
    removalAction === 'unlink' ? riskData?.risk[0] : appetite;
  const { hasPermission: hasRemovalPermission, loading: permissionLoading } =
    useHasPermissionQuery(removalPermission, removalParent);
  const canRemoveAppetite =
    removalAction !== 'unavailable' &&
    hasRemovalPermission &&
    !permissionLoading;

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await removeAppetites([appetite!.Id]);
      setIsDeleteModalVisible(false);
      navigate('..');

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const title = getFriendlyId(
    Parent_Type_Enum.Appetite,
    appetite?.SequentialId
  );

  // Object-level toolbar (CUS-159) — flag-gated. Relocates Delete into the
  // toolbar's danger ⋮ slot and surfaces the Save disc / Cancel X (save state
  // bridged from the active tab's form via useObjectToolbarFormStore). Appetites
  // have no per-object export, so no download group. Tab settings move into the
  // overflow "Edit" group (ControlledTabs' own gear is hidden with the flag on).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title,
      },
      parentObject: appetite,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: canRemoveAppetite
          ? [
              {
                id: 'delete',
                label: t('delete'),
                iconName: 'trash-01',
                // Hydration gate (ENG-616) — same as risks/assets.
                disabled: !appetite,
                onClick: () => setIsDeleteModalVisible(true),
              },
            ]
          : [],
      },
    }),
    [title, appetite, t, formConfig, canRemoveAppetite]
  );

  if (loading || (riskId && (riskLoading || aggregationLoading))) {
    return (
      <PageLayout>
        <Loading />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      helpTranslationKey={'appetites.help'}
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {canRemoveAppetite && (
            <Permission
              permission={removalPermission}
              parentObject={removalParent}
            >
              <Button
                variant={'normal'}
                formAction={'none'}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {t('delete')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Appetite}
        parent={appetite}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Appetite}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={appetite}
      />
      <DeleteModal
        loading={removeLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
